import { Copy, Pencil, Scissors, StickyNote, Trash2, Video } from 'lucide-react'
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { pendingAbsenceStyle } from '../../lib/absence-style'
import type {
  Absence,
  AbsenceType,
  Meeting,
  Nonbusinessday,
  Project,
  TimeEntry,
} from '../../lib/api'
import { colorForKey, dayKey, isSameDay } from '../../lib/calendar'
import { displayAbsenceForDay } from '../../lib/calendar-overlays'
import { hasNotes, showInlineNotes, showProjectLine } from '../../lib/entry-notes-display'
import { entryTooltip } from '../../lib/entry-tooltip'
import { MIN_GAP_MINUTES } from '../../lib/gaps'
import { computeHourHeight, HOUR_HEIGHT_MIN } from '../../lib/hour-height'
import { splitRangeForSegment } from '../../lib/split-range'
import {
  classifyBlockTouchEnd,
  isCreateDragSizing,
  isSelectionGhostClick,
  isTapMovement,
  LONG_PRESS_MS,
  longPressCreateSpan,
  nextSelectedId,
  resizeZoneAt,
  SELECTION_CLICK_GUARD_MS,
  TOUCH_RESIZE_HIT,
  TOUCH_RESIZE_OVERHANG,
} from '../../lib/touch-selection'
import { cn, formatHM } from '../../lib/utils'

const SNAP_MINUTES = 15
const CLICK_THRESHOLD = 4
const RESIZE_HANDLE_HEIGHT = 15
// Outside variant (sits ABOVE/BELOW the block instead of inside it) so the
// block itself is a clean drag target without grab-zone overlap.
const RESIZE_HANDLE_OUTSIDE = 6
const DAY_MINUTES = 24 * 60

type Segment = {
  startMins: number
  endMins: number
  isStartDay: boolean
  isEndDay: boolean
}

// Returns the visible segment of an entry inside a single calendar day,
// expressed as minutes-since-00:00. Handles entries that cross midnight by
// clamping to [0, 1440]. Returns null if the entry doesn't touch this day.
function entrySegmentForDay(entry: TimeEntry, day: Date): Segment | null {
  const start = new Date(entry.startTime).getTime()
  const end = entry.endTime ? new Date(entry.endTime).getTime() : Date.now()
  const dayStart = new Date(day)
  dayStart.setHours(0, 0, 0, 0)
  const dayStartMs = dayStart.getTime()
  const dayEndMs = dayStartMs + DAY_MINUTES * 60_000
  const segStart = Math.max(start, dayStartMs)
  const segEnd = Math.min(end, dayEndMs)
  if (segEnd <= segStart) return null
  return {
    startMins: Math.round((segStart - dayStartMs) / 60_000),
    endMins: Math.round((segEnd - dayStartMs) / 60_000),
    isStartDay: start >= dayStartMs && start < dayEndMs,
    isEndDay: end > dayStartMs && end <= dayEndMs,
  }
}

// Same shape for meetings — endTime is always set.
function meetingSegmentForDay(meeting: Meeting, day: Date): Segment | null {
  const start = new Date(meeting.startTime).getTime()
  const end = new Date(meeting.endTime).getTime()
  const dayStart = new Date(day)
  dayStart.setHours(0, 0, 0, 0)
  const dayStartMs = dayStart.getTime()
  const dayEndMs = dayStartMs + DAY_MINUTES * 60_000
  const segStart = Math.max(start, dayStartMs)
  const segEnd = Math.min(end, dayEndMs)
  if (segEnd <= segStart) return null
  return {
    startMins: Math.round((segStart - dayStartMs) / 60_000),
    endMins: Math.round((segEnd - dayStartMs) / 60_000),
    isStartDay: start >= dayStartMs && start < dayEndMs,
    isEndDay: end > dayStartMs && end <= dayEndMs,
  }
}

export interface DraftRange {
  startTime: Date
  endTime: Date
  day: Date
}

type Action =
  | {
      kind: 'create'
      dayIndex: number
      anchorY: number
      currentY: number
      currentDayIndex: number
      /** Long-Press-Create am Touch: eigene Endzeit-Regeln (stationär =
       *  Start+1h, aufgezogen = Snap-Spanne mit Mindestdauer) statt der
       *  Desktop-CLICK_THRESHOLD-Heuristik. Maus/Pen setzen das nie. */
      touch?: boolean
    }
  | {
      kind: 'move'
      entryId: string
      dayIndex: number
      origStartMins: number
      durationMins: number
      pointerStartY: number
      currentY: number
      currentDayIndex: number
      /** Strg/Cmd beim Drag-Start: der Commit legt eine KOPIE am Zielort an,
       *  statt das Original zu verschieben. */
      copy?: boolean
    }
  | {
      kind: 'resize'
      side: 'start' | 'end'
      entryId: string
      dayIndex: number
      origStartMins: number
      origEndMins: number
      currentY: number
    }
  | {
      kind: 'meeting-move'
      meetingId: string
      dayIndex: number
      origStartMins: number
      durationMins: number
      pointerStartY: number
      currentY: number
      currentDayIndex: number
    }
  | {
      kind: 'meeting-resize'
      side: 'start' | 'end'
      meetingId: string
      dayIndex: number
      origStartMins: number
      origEndMins: number
      currentY: number
    }

interface HourGridProps {
  days: Date[]
  entries: TimeEntry[]
  projects: Map<string, Project>
  holidays?: Map<string, Nonbusinessday>
  absences?: Absence[]
  /** Online meetings to render as ghost-blocks behind time entries. */
  meetings?: Meeting[]
  onMeetingClick?: (meeting: Meeting) => void
  onMeetingMove?: (meetingId: string, startTime: Date, endTime: Date) => void
  onMeetingResize?: (meetingId: string, range: { startTime?: Date; endTime?: Date }) => void
  onMeetingDelete?: (meeting: Meeting) => void
  onMeetingJoin?: (meeting: Meeting) => void
  onEntryClick: (entry: TimeEntry) => void
  onCreate: (draft: DraftRange) => void
  onMove: (entryId: string, newStart: Date, newEnd: Date) => void
  /** Strg+Drag: Kopie des Eintrags am Zielort anlegen (Original bleibt). */
  onCopy?: (entryId: string, newStart: Date, newEnd: Date) => void
  /** Menü-Option „Duplizieren": Kopie direkt im Anschluss ans Original. */
  onDuplicate?: (entryId: string) => void
  onResize: (entryId: string, range: { startTime?: Date; endTime?: Date }) => void
  onDelete: (entryId: string) => void
  /** Split an entry at an explicit ISO timestamp (chosen via the in-grid
   *  split line). */
  onSplit: (entryId: string, splitAt: string) => void
  footer?: React.ReactNode
  /** Hide the sticky weekday/date header row. Used by the mobile week view,
   *  where the day tabs above the grid already carry the same info (A10). */
  hideDayHeader?: boolean
  /** View-As fremder User ohne Editier-Recht: die Callbacks sind dann zwar
   *  schon No-ops, aber die Touch-Gesten (Long-Press-Create) dürfen gar
   *  nicht erst armieren — keine Vibration/Preview ohne Wirkung. */
  readOnly?: boolean
}

// Used only to tint the column body when the user is absent that day —
// the chip + label live in the WeekFooter strip.
const ABSENCE_COLOR: Record<AbsenceType, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
  overtime_reduction: '#A3978C',
  maternity: '#A3978C',
  parental: '#A3978C',
}

const EMPTY_HOLIDAYS = new Map<string, Nonbusinessday>()
const EMPTY_ABSENCES: Absence[] = []
const EMPTY_MEETINGS: Meeting[] = []

interface LaidOutEntry {
  entry: TimeEntry
  segment: Segment
  col: number
  cols: number
}

// Greedy column packing for overlapping entries within one day.
// Entries are clustered by overlap chain; within each cluster every entry
// shares the same `cols` count so they all line up to the same width.
// O(n²) over a single day's entries — fine for realistic counts.
function layoutDayEntries(entries: TimeEntry[], day: Date): LaidOutEntry[] {
  const items = entries
    .map((e) => ({ entry: e, segment: entrySegmentForDay(e, day) }))
    .filter((x): x is { entry: TimeEntry; segment: Segment } => x.segment !== null)
    .sort((a, b) => a.segment.startMins - b.segment.startMins)

  const result: LaidOutEntry[] = []
  let cluster: { item: { entry: TimeEntry; segment: Segment }; col: number }[] = []
  let clusterEnd = -1
  let clusterMaxCol = 0

  const flush = () => {
    const cols = clusterMaxCol + 1
    for (const c of cluster) {
      result.push({ entry: c.item.entry, segment: c.item.segment, col: c.col, cols })
    }
    cluster = []
    clusterMaxCol = 0
    clusterEnd = -1
  }

  for (const item of items) {
    if (item.segment.startMins >= clusterEnd) flush()
    const taken = new Set(
      cluster.filter((c) => c.item.segment.endMins > item.segment.startMins).map((c) => c.col),
    )
    let col = 0
    while (taken.has(col)) col++
    cluster.push({ item, col })
    if (col > clusterMaxCol) clusterMaxCol = col
    if (item.segment.endMins > clusterEnd) clusterEnd = item.segment.endMins
  }
  flush()
  return result
}

export function HourGrid({
  days,
  entries,
  projects,
  holidays = EMPTY_HOLIDAYS,
  absences = EMPTY_ABSENCES,
  meetings = EMPTY_MEETINGS,
  onMeetingClick,
  onMeetingMove,
  onMeetingResize,
  onMeetingDelete,
  onMeetingJoin,
  onEntryClick,
  onCreate,
  onMove,
  onCopy,
  onDuplicate,
  onResize,
  onDelete,
  onSplit,
  footer,
  hideDayHeader = false,
  readOnly = false,
}: HourGridProps) {
  const columnsRef = useRef<(HTMLDivElement | null)[]>([])
  const actionRef = useRef<Action | null>(null)
  const [action, setActionState] = useState<Action | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [touchDragging, setTouchDragging] = useState(false)
  const touchStartPos = useRef<{ x: number; y: number } | null>(null)
  const gridScrollRef = useRef<HTMLDivElement>(null)
  // Hover-Zeitmarker (Ticket: „Auswahl links am Rand visualisieren"): der
  // gesnappte 15-min-Slot unter der Maus, als Tick + HH:MM-Pill an der
  // Zeitachse gerendert. Maus-only — Touch kennt kein echtes Hover, ein
  // Sticky-Hover nach Tap würde nur einen stale Marker hinterlassen.
  const [hoverSlot, setHoverSlot] = useState<{ dayIndex: number; mins: number } | null>(null)
  // In-grid split mode: which entry is being split + the current (snapped)
  // split minute and the legal range [minMins, maxMins] = [start+15, end-15].
  const [splitTarget, setSplitTarget] = useState<{
    entryId: string
    dayIndex: number
    mins: number
    minMins: number
    maxMins: number
  } | null>(null)

  // Touch-Selektionsmodus (Option B, wie Google Calendar mobil): der per Tap
  // selektierte Block bekommt touch-action:none, Ring-Highlight, Action-Bar
  // und große Resize-Griffe — erst DANN ist Direkt-Drag/-Resize möglich.
  // Maus/Pen setzen diesen State nie; Desktop bleibt hover-basiert.
  const [selected, setSelected] = useState<{ type: 'entry' | 'meeting'; id: string } | null>(null)
  // Zeitstempel des SELEKTIERENDEN Taps: der Browser feuert nach pointerup
  // einen synthetischen click an denselben Koordinaten, der sonst die soeben
  // eingeblendete Action-Bar träfe (mittig = Teilen, rechts = Löschen!).
  const selectedAtRef = useRef<number | null>(null)
  const selectBlock = (type: 'entry' | 'meeting', id: string | null) =>
    setSelected((cur) => {
      const next = nextSelectedId(cur?.id ?? null, id)
      if (next !== null && next !== cur?.id) selectedAtRef.current = performance.now()
      return next === null ? null : { type, id: next }
    })
  const swallowSelectionGhostClick = (e: ReactMouseEvent) => {
    if (isSelectionGhostClick(selectedAtRef.current, performance.now())) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  // Reaktive Stundenhöhe: früher einmalig beim Modul-Load aus
  // window.innerHeight abgeleitet — Rotation/Resize blieben bis zum Reload
  // auf der falschen Skala. Formel unverändert (Desktop-Werte identisch).
  const [hourHeight, setHourHeight] = useState(() =>
    typeof window === 'undefined' ? HOUR_HEIGHT_MIN : computeHourHeight(window.innerHeight),
  )
  useEffect(() => {
    const update = () => setHourHeight(computeHourHeight(window.innerHeight))
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])

  // Full 24h is always rendered — the early/late collapse toggles were
  // removed by user request. The day-column scroll context handles the
  // overflow.
  const visibleStartHour = 0
  const visibleEndHour = 24
  const visibleHours = visibleEndHour - visibleStartHour

  const setAction = (a: Action | null) => {
    actionRef.current = a
    setActionState(a)
  }

  const yToMinutes = (y: number) => {
    const m = (y / hourHeight + visibleStartHour) * 60
    return Math.max(0, Math.min(24 * 60, Math.round(m / SNAP_MINUTES) * SNAP_MINUTES))
  }
  const minutesToY = (m: number) => (m / 60 - visibleStartHour) * hourHeight

  const columnUnderX = (clientX: number): number | null => {
    for (let i = 0; i < columnsRef.current.length; i++) {
      const col = columnsRef.current[i]
      if (!col) continue
      const r = col.getBoundingClientRect()
      if (clientX >= r.left && clientX <= r.right) return i
    }
    return null
  }

  const yInColumn = (clientY: number, columnIndex: number): number | null => {
    const col = columnsRef.current[columnIndex]
    if (!col) return null
    return clientY - col.getBoundingClientRect().top
  }

  // Enter split mode for an entry, seeding the split line at the (snapped)
  // midpoint. Die legale Range kommt aus splitRangeForSegment: Abstand nur zu
  // ECHTEN Eintragsgrenzen — an Tagesgrenzen (Mitternachts-Einträge) ist der
  // Split bis exakt 00:00/24:00 erlaubt (kanonischer Mitternachts-Split, war
  // vorher durch die pauschale ±15-min-Klemme unmöglich).
  const enterSplit = (entryId: string, dayIndex: number, seg: Segment) => {
    const range = splitRangeForSegment(seg.startMins, seg.endMins, seg.isStartDay, seg.isEndDay)
    if (!range) return // unteilbar — der Button ist dann bereits disabled
    const { minMins, maxMins } = range
    const rawMid = Math.round((seg.startMins + seg.endMins) / 2 / SNAP_MINUTES) * SNAP_MINUTES
    const mins = Math.min(maxMins, Math.max(minMins, rawMid))
    setSplitTarget({ entryId, dayIndex, mins, minMins, maxMins })
  }

  // Drag the split line. Self-contained pointer handlers (like meeting-resize)
  // so mouse + touch both work via pointer events; touchAction:'none' on the
  // grabber prevents the page from scrolling mid-drag.
  const onSplitGrabPointerDown = (e: ReactPointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const move = (ev: PointerEvent) => {
      setSplitTarget((cur) => {
        if (!cur) return cur
        const y = yInColumn(ev.clientY, cur.dayIndex)
        if (y == null) return cur
        const mins = Math.min(cur.maxMins, Math.max(cur.minMins, yToMinutes(y)))
        return { ...cur, mins }
      })
    }
    const up = () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.removeEventListener('pointercancel', up)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    // pointercancel (Scroll-/System-Takeover) räumt genauso auf wie up —
    // der Split committet ohnehin erst über den Teilen-Button.
    document.addEventListener('pointercancel', up)
  }

  // Esc cancels split mode.
  useEffect(() => {
    if (!splitTarget) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSplitTarget(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [splitTarget])

  // Document-level pointermove + pointerup while an action is active.
  // We deliberately depend only on `actionActive` (boolean) — handlers read
  // the latest action via actionRef so we don't need to re-attach on every
  // mouse-move state update. Helpers and `commit` are stable closures over
  // refs, so omitting them from deps is intentional.
  const actionActive = action !== null
  // biome-ignore lint/correctness/useExhaustiveDependencies: ref-based access by design
  useEffect(() => {
    if (!actionActive) return
    const onMoveDoc = (e: PointerEvent) => {
      const cur = actionRef.current
      if (!cur) return
      if (cur.kind === 'resize' || cur.kind === 'meeting-resize') {
        const y = yInColumn(e.clientY, cur.dayIndex)
        if (y == null) return
        // Bail out when the SNAPPED position hasn't changed. Gaming mice fire
        // 500–1000 pointermove/s; without this every raw pixel move re-renders
        // the whole grid (7× layoutDayEntries) even between 15-min snap steps.
        if (yToMinutes(y) === yToMinutes(cur.currentY)) return
        setAction({ ...cur, currentY: y })
        return
      }
      const dayIdx = columnUnderX(e.clientX)
      const targetIdx = dayIdx ?? cur.currentDayIndex
      const y = yInColumn(e.clientY, targetIdx)
      if (y == null) return
      if (cur.kind === 'create' || cur.kind === 'move' || cur.kind === 'meeting-move') {
        // Skip redundant state-sets when neither the target column nor the
        // snapped vertical position changed (same rationale as resize above).
        if (targetIdx === cur.currentDayIndex && yToMinutes(y) === yToMinutes(cur.currentY)) return
        setAction({ ...cur, currentY: y, currentDayIndex: targetIdx })
      }
    }
    const onUpDoc = () => {
      const cur = actionRef.current
      if (!cur) return
      commit(cur)
      setAction(null)
      setTouchDragging(false)
      clearLongPress()
    }
    const onCancelDoc = () => {
      // Browser hat die Geste übernommen (Scroll-Takeover, Systemgeste,
      // eingehender Anruf): Action verwerfen und NIEMALS committen — sonst
      // klemmt das Grid mit touchAction:'none' und der nächste Tap feuert
      // einen Geister-Commit der stalen Action. Die Selektion bleibt
      // absichtlich bestehen (frustfrei erneut ansetzen).
      if (!actionRef.current) return
      setAction(null)
      setTouchDragging(false)
      clearLongPress()
    }
    document.addEventListener('pointermove', onMoveDoc)
    document.addEventListener('pointerup', onUpDoc)
    document.addEventListener('pointercancel', onCancelDoc)
    return () => {
      document.removeEventListener('pointermove', onMoveDoc)
      document.removeEventListener('pointerup', onUpDoc)
      document.removeEventListener('pointercancel', onCancelDoc)
    }
  }, [actionActive])

  // Einmaliger capture-click-Listener nach einem committeten Long-Press-
  // Create: der Browser synthetisiert nach pointerup einen click an den
  // Press-Koordinaten — der träfe das soeben aufgehende Erfassen-Sheet
  // (schlimmster Fall: Backdrop ⇒ Sheet schließt sofort wieder). Gleiche
  // Fenster-Logik wie beim Selektions-Tap (isSelectionGhostClick).
  const armCreateGhostClickGuard = () => {
    const stamp = performance.now()
    const onClickCapture = (ev: MouseEvent) => {
      document.removeEventListener('click', onClickCapture, true)
      if (isSelectionGhostClick(stamp, performance.now())) {
        ev.preventDefault()
        ev.stopPropagation()
      }
    }
    document.addEventListener('click', onClickCapture, true)
    // Kommt kein synthetischer Click, darf der Guard keinen späteren echten
    // Click schlucken — nach Ablauf des Fensters selbst abbauen.
    window.setTimeout(
      () => document.removeEventListener('click', onClickCapture, true),
      SELECTION_CLICK_GUARD_MS,
    )
  }

  const commit = (a: Action) => {
    if (a.kind === 'create') {
      const day = days[a.currentDayIndex]
      if (a.touch) {
        // Long-Press-Create: stationär = Start+1h (Desktop-Klick-Parität),
        // aufgezogen (≥1 Snap-Schritt Fingerweg) = Snap-Spanne in beide
        // Richtungen mit Mindestdauer — pure Logik in touch-selection.ts.
        const sized = isCreateDragSizing(a.currentY - a.anchorY, hourHeight, SNAP_MINUTES)
        const { startMins, endMins } = longPressCreateSpan({
          anchorMins: yToMinutes(a.anchorY),
          currentMins: yToMinutes(a.currentY),
          sized,
          snapMinutes: SNAP_MINUTES,
        })
        armCreateGhostClickGuard()
        onCreate({
          startTime: makeDateTime(day, startMins),
          endTime: makeDateTime(day, endMins),
          day,
        })
        return
      }
      const yA = Math.min(a.anchorY, a.currentY)
      const yB = Math.max(a.anchorY, a.currentY)
      const startMins = yToMinutes(yA)
      let endMins = yToMinutes(yB)
      if (Math.abs(yB - yA) < CLICK_THRESHOLD) endMins = startMins + 60
      if (endMins <= startMins) endMins = startMins + 30
      const startTime = makeDateTime(day, startMins)
      const endTime = makeDateTime(day, endMins)
      onCreate({ startTime, endTime, day })
      return
    }
    if (a.kind === 'move') {
      const deltaY = a.currentY - a.pointerStartY
      const sameDay = a.dayIndex === a.currentDayIndex
      // Plain click (no drag) is now a no-op — editing happens via the
      // pencil icon. We still need this guard so a click doesn't fire a
      // zero-distance move mutation.
      if (Math.abs(deltaY) < CLICK_THRESHOLD && sameDay) return
      const newStartMins = yToMinutes(minutesToY(a.origStartMins) + deltaY)
      // Multi-day entries: don't try to squeeze them into one column —
      // clamp start to [0, 1440) and let the end overflow into the next day.
      const maxStart =
        a.durationMins <= DAY_MINUTES ? DAY_MINUTES - a.durationMins : DAY_MINUTES - 1
      const clampedStart = Math.max(0, Math.min(maxStart, newStartMins))
      const newEndMins = clampedStart + a.durationMins
      const day = days[a.currentDayIndex]
      if (a.copy && onCopy) {
        onCopy(a.entryId, makeDateTime(day, clampedStart), makeDateTime(day, newEndMins))
      } else {
        onMove(a.entryId, makeDateTime(day, clampedStart), makeDateTime(day, newEndMins))
      }
      return
    }
    if (a.kind === 'meeting-move') {
      const deltaY = a.currentY - a.pointerStartY
      const sameDay = a.dayIndex === a.currentDayIndex
      if (Math.abs(deltaY) < CLICK_THRESHOLD && sameDay) return
      const newStartMins = yToMinutes(minutesToY(a.origStartMins) + deltaY)
      const maxStart =
        a.durationMins <= DAY_MINUTES ? DAY_MINUTES - a.durationMins : DAY_MINUTES - 1
      const clampedStart = Math.max(0, Math.min(maxStart, newStartMins))
      const newEndMins = clampedStart + a.durationMins
      const day = days[a.currentDayIndex]
      onMeetingMove?.(a.meetingId, makeDateTime(day, clampedStart), makeDateTime(day, newEndMins))
      return
    }
    if (a.kind === 'meeting-resize') {
      const day = days[a.dayIndex]
      if (a.side === 'end') {
        let newEndMins = yToMinutes(a.currentY)
        if (newEndMins <= a.origStartMins) newEndMins = a.origStartMins + SNAP_MINUTES
        onMeetingResize?.(a.meetingId, { endTime: makeDateTime(day, newEndMins) })
      } else {
        let newStartMins = yToMinutes(a.currentY)
        if (newStartMins >= a.origEndMins) newStartMins = a.origEndMins - SNAP_MINUTES
        if (newStartMins < 0) newStartMins = 0
        onMeetingResize?.(a.meetingId, { startTime: makeDateTime(day, newStartMins) })
      }
      return
    }
    // resize (time entry)
    const day = days[a.dayIndex]
    if (a.side === 'end') {
      let newEndMins = yToMinutes(a.currentY)
      if (newEndMins <= a.origStartMins) newEndMins = a.origStartMins + SNAP_MINUTES
      onResize(a.entryId, { endTime: makeDateTime(day, newEndMins) })
    } else {
      let newStartMins = yToMinutes(a.currentY)
      if (newStartMins >= a.origEndMins) newStartMins = a.origEndMins - SNAP_MINUTES
      if (newStartMins < 0) newStartMins = 0
      onResize(a.entryId, { startTime: makeDateTime(day, newStartMins) })
    }
  }

  const onColumnPointerDown = (e: ReactPointerEvent, dayIndex: number) => {
    if (e.button !== 0) return
    // Touch: Tap auf freie Spaltenfläche verlässt den Selektionsmodus;
    // Long-Press (400ms) armiert Create-Drag (Preview + Aufziehen), sofort
    // loslassen = Start+1h. Mouse/pen: drag-to-create wie bisher.
    if (e.pointerType === 'touch') {
      setSelected(null)
      if (readOnly) return
      beginEmptyColumnLongPress(e, dayIndex)
      return
    }
    const y = yInColumn(e.clientY, dayIndex)
    if (y == null) return
    setAction({
      kind: 'create',
      dayIndex,
      anchorY: y,
      currentY: y,
      currentDayIndex: dayIndex,
    })
  }

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  // Long-Press auf LEERER Spaltenfläche (nur Touch): armiert nach 400ms die
  // bestehende kind:'create'-Action (Preview + Document-Handler übernehmen
  // Move/Commit). Aufbau nach dem Muster von beginTouchInteraction: Move
  // über dem Slop vor Ablauf = Scroll ⇒ Abbruch; pointerup vor Ablauf =
  // Tap ⇒ nichts; pointercancel (Scroll-Takeover) räumt den Timer zwingend.
  // Scroll-Sperre NACH dem Feuern: die Spalte stand beim pointerdown auf
  // touch-action:pan-y und die Geste ist damit eingefroren — nur ein
  // non-passive touchmove-preventDefault hält den Browser jetzt noch vom
  // Scroll-Takeover (pointercancel!) ab, solange der Finger aufspannt.
  const beginEmptyColumnLongPress = (e: ReactPointerEvent, dayIndex: number) => {
    const el = e.currentTarget as HTMLElement
    const pointerId = e.pointerId
    const startX = e.clientX
    const startY = e.clientY
    clearLongPress()
    let fired = false
    const preventTouchScroll = (ev: TouchEvent) => ev.preventDefault()
    const removeListeners = () => {
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointercancel', onCancel)
      document.removeEventListener('touchmove', preventTouchScroll)
    }
    longPressTimer.current = setTimeout(() => {
      const y = yInColumn(startY, dayIndex)
      if (y == null) {
        removeListeners()
        return
      }
      fired = true
      navigator.vibrate?.(10)
      document.addEventListener('touchmove', preventTouchScroll, { passive: false })
      setTouchDragging(true)
      setAction({
        kind: 'create',
        dayIndex,
        anchorY: y,
        currentY: y,
        currentDayIndex: dayIndex,
        touch: true,
      })
      try {
        el.setPointerCapture(pointerId)
      } catch {
        // Pointer kann inzwischen weg sein — Capture ist nur Optimierung.
      }
    }, LONG_PRESS_MS)
    const onUp = () => {
      // Nach gefeuertem Long-Press committet der Document-Effect (onUpDoc)
      // die Create-Action — hier nur lokale Listener + Timer räumen.
      removeListeners()
      clearLongPress()
      if (!fired) setTouchDragging(false)
    }
    const onMove = (ev: PointerEvent) => {
      if (fired) return
      if (!isTapMovement(ev.clientX - startX, ev.clientY - startY)) {
        // Scroll hat übernommen — kein Long-Press mehr.
        clearLongPress()
        removeListeners()
      }
    }
    const onCancel = () => {
      // NIEMALS committen — eine bereits armierte Action verwirft zusätzlich
      // der document-level pointercancel-Handler (onCancelDoc).
      removeListeners()
      clearLongPress()
      setTouchDragging(false)
    }
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointercancel', onCancel)
  }

  const startDragForEntry = (entry: TimeEntry, dayIndex: number, clientY: number) => {
    if (entry.endTime === null) return
    const seg = entrySegmentForDay(entry, days[dayIndex])
    if (!seg?.isStartDay) return
    const y = yInColumn(clientY, dayIndex)
    if (y == null) return
    const start = new Date(entry.startTime).getTime()
    const end = new Date(entry.endTime).getTime()
    setTouchDragging(true)
    setAction({
      kind: 'move',
      entryId: entry.id,
      dayIndex,
      origStartMins: seg.startMins,
      durationMins: Math.round((end - start) / 60_000),
      pointerStartY: y,
      currentY: y,
      currentDayIndex: dayIndex,
    })
  }

  const startDragForMeeting = (meeting: Meeting, dayIndex: number, clientY: number) => {
    if (!onMeetingMove) return
    const seg = meetingSegmentForDay(meeting, days[dayIndex])
    if (!seg?.isStartDay) return
    const y = yInColumn(clientY, dayIndex)
    if (y == null) return
    const start = new Date(meeting.startTime).getTime()
    const end = new Date(meeting.endTime).getTime()
    setTouchDragging(true)
    setAction({
      kind: 'meeting-move',
      meetingId: meeting.id,
      dayIndex,
      origStartMins: seg.startMins,
      durationMins: Math.round((end - start) / 60_000),
      pointerStartY: y,
      currentY: y,
      currentDayIndex: dayIndex,
    })
  }

  // Resize eines Zeiteintrags aus dem Touch-Selektionsmodus heraus —
  // gleiche Guards wie onResizePointerDown (laufender Timer, Tag-Kanten).
  const startTouchResizeForEntry = (
    entry: TimeEntry,
    dayIndex: number,
    side: 'start' | 'end',
    clientY: number,
  ) => {
    if (entry.endTime === null) return
    const seg = entrySegmentForDay(entry, days[dayIndex])
    if (!seg) return
    if (side === 'start' && !seg.isStartDay) return
    if (side === 'end' && !seg.isEndDay) return
    const y = yInColumn(clientY, dayIndex)
    if (y == null) return
    setTouchDragging(true)
    setAction({
      kind: 'resize',
      side,
      entryId: entry.id,
      dayIndex,
      origStartMins: seg.startMins,
      origEndMins: seg.endMins,
      currentY: y,
    })
  }

  const startTouchResizeForMeeting = (
    meeting: Meeting,
    dayIndex: number,
    side: 'start' | 'end',
    clientY: number,
  ) => {
    if (!onMeetingResize) return
    const seg = meetingSegmentForDay(meeting, days[dayIndex])
    if (!seg) return
    if (side === 'start' && !seg.isStartDay) return
    if (side === 'end' && !seg.isEndDay) return
    const y = yInColumn(clientY, dayIndex)
    if (y == null) return
    setTouchDragging(true)
    setAction({
      kind: 'meeting-resize',
      side,
      meetingId: meeting.id,
      dayIndex,
      origStartMins: seg.startMins,
      origEndMins: seg.endMins,
      currentY: y,
    })
  }

  // Touch-pointerdown auf dem SELEKTIERTEN Block: der Block trägt
  // touch-action:'none', darum startet die Geste sofort (kein Long-Press).
  // Der Zonen-Hit-Test (resizeZoneAt) entscheidet Move vs. Start-/End-Resize
  // — die großen Griff-Elemente ragen über die Blockkanten hinaus und
  // bubbeln hierher, deshalb darf offsetY auch negativ/über der Höhe liegen.
  const onSelectedBlockTouchDown = (
    e: ReactPointerEvent,
    startMove: (clientY: number) => void,
    startResize: (side: 'start' | 'end', clientY: number) => void,
  ) => {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const zone = resizeZoneAt(e.clientY - rect.top, rect.height)
    try {
      el.setPointerCapture(e.pointerId)
    } catch {
      // Capture ist nur Absicherung gegen schnelles Abgleiten — optional.
    }
    if (zone === 'move') startMove(e.clientY)
    else startResize(zone, e.clientY)
  }

  // Touch-pointerdown auf einem NICHT selektierten Block: kurzer Tap
  // selektiert, Long-Press (400ms) selektiert + armiert den Drag in einem
  // Zug (mit Vibrations-Feedback). Bewegung über dem Slop vor Ablauf des
  // Long-Press = Scroll → Long-Press abbrechen, nichts committen.
  const beginTouchInteraction = (
    e: ReactPointerEvent,
    select: () => void,
    armDrag: (clientY: number) => void,
  ) => {
    const el = e.currentTarget as HTMLElement
    const pointerId = e.pointerId
    const startX = e.clientX
    const startY = e.clientY
    touchStartPos.current = { x: startX, y: startY }
    clearLongPress()
    let longPressFired = false
    longPressTimer.current = setTimeout(() => {
      longPressFired = true
      // Arming-Feedback: Selektion (Ring/Shadow am Block) + Haptik; der
      // Drag selbst zeigt sofort den Preview-Block.
      select()
      navigator.vibrate?.(10)
      armDrag(startY)
      try {
        el.setPointerCapture(pointerId)
      } catch {
        // Pointer kann inzwischen weg sein — Capture ist nur Optimierung.
      }
    }, LONG_PRESS_MS)
    const removeListeners = () => {
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointercancel', onCancel)
    }
    const onUp = (ev: PointerEvent) => {
      removeListeners()
      clearLongPress()
      const kind = classifyBlockTouchEnd({
        pointerType: ev.pointerType,
        dx: ev.clientX - startX,
        dy: ev.clientY - startY,
        longPressFired,
      })
      if (kind === 'tap') select()
      // Nach gefeuertem Long-Press räumt der Document-Effect (commit) auf.
      if (!longPressFired) setTouchDragging(false)
    }
    const onMove = (ev: PointerEvent) => {
      if (longPressFired) return
      if (!isTapMovement(ev.clientX - startX, ev.clientY - startY)) {
        // Scroll hat übernommen — kein Tap, kein Long-Press mehr.
        clearLongPress()
        removeListeners()
      }
    }
    const onCancel = () => {
      // NIEMALS committen — nur aufräumen. Eine bereits armierte Action
      // verwirft zusätzlich der document-level pointercancel-Handler.
      removeListeners()
      clearLongPress()
      setTouchDragging(false)
    }
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointercancel', onCancel)
  }

  const onBlockPointerDown = (e: ReactPointerEvent, entry: TimeEntry, dayIndex: number) => {
    e.stopPropagation()
    if (e.button !== 0) return

    if (e.pointerType === 'touch') {
      const isSelected = selected?.type === 'entry' && selected.id === entry.id
      if (isSelected) {
        // Laufende Einträge: selektierbar (Action-Bar), aber nie drag-/
        // resizebar — Geometrie ohne definiertes Ende (Guard wie Desktop).
        if (entry.endTime === null) return
        onSelectedBlockTouchDown(
          e,
          (clientY) => startDragForEntry(entry, dayIndex, clientY),
          (side, clientY) => startTouchResizeForEntry(entry, dayIndex, side, clientY),
        )
        return
      }
      beginTouchInteraction(
        e,
        () => selectBlock('entry', entry.id),
        (clientY) => startDragForEntry(entry, dayIndex, clientY),
      )
      return
    }

    // Mouse/pen: immediate drag
    if (entry.endTime === null) return
    const seg = entrySegmentForDay(entry, days[dayIndex])
    if (!seg?.isStartDay) return
    const y = yInColumn(e.clientY, dayIndex)
    if (y == null) return
    const start = new Date(entry.startTime).getTime()
    const end = new Date(entry.endTime).getTime()
    setAction({
      kind: 'move',
      entryId: entry.id,
      dayIndex,
      origStartMins: seg.startMins,
      durationMins: Math.round((end - start) / 60_000),
      pointerStartY: y,
      currentY: y,
      currentDayIndex: dayIndex,
      // Strg/Cmd + Ziehen = Kopieren (Ticket) — nur Maus/Pen, Touch hat
      // keine Modifier und nutzt die Duplizieren-Menü-Option.
      copy: (e.ctrlKey || e.metaKey) && onCopy != null,
    })
  }

  const onResizePointerDown = (
    e: ReactPointerEvent,
    entry: TimeEntry,
    dayIndex: number,
    side: 'start' | 'end',
  ) => {
    e.stopPropagation()
    e.preventDefault()
    if (e.pointerType === 'touch') {
      setTouchDragging(true)
    }
    if (e.button !== 0 || entry.endTime === null) return
    const seg = entrySegmentForDay(entry, days[dayIndex])
    if (!seg) return
    // Top handle only on the start-day column; bottom handle only on the end-day column.
    if (side === 'start' && !seg.isStartDay) return
    if (side === 'end' && !seg.isEndDay) return
    const y = yInColumn(e.clientY, dayIndex)
    if (y == null) return
    setAction({
      kind: 'resize',
      side,
      entryId: entry.id,
      dayIndex,
      origStartMins: seg.startMins,
      origEndMins: seg.endMins,
      currentY: y,
    })
  }

  const now = new Date()

  return (
    <div
      className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex-1 flex flex-col"
      style={{ touchAction: touchDragging || action ? 'none' : 'auto' }}
    >
      {!hideDayHeader && (
        <div
          className="grid sticky top-0 z-20 bg-bg-elevated/95 backdrop-blur-md border-b border-border-subtle"
          style={{ gridTemplateColumns: `48px repeat(${days.length}, 1fr)` }}
        >
          <div />
          {days.map((d) => {
            const today = isSameDay(d, now)
            // Header carries the date and (only) the Feiertag chip. Absence
            // labels live in the WeekFooter below the grid; the column body
            // is tinted instead so the visual cue stays without duplication.
            const holiday = holidays.get(dayKey(d)) ?? null
            return (
              <div
                key={d.toISOString()}
                className={cn(
                  'px-3 py-2.5 text-xs',
                  today ? 'text-accent' : 'text-text-secondary',
                  holiday && 'bg-warning/5',
                )}
              >
                {/* Holiday chip removed — the WeekFooter below already shows
                   the full holiday name (e.g. 'Pfingstmontag') to avoid the
                   'Feiertag' + 'Pfingstmontag' double-label. */}
                <div className="flex items-center gap-2 uppercase tracking-wider flex-wrap">
                  <span>{d.toLocaleDateString('de-DE', { weekday: 'short' })}</span>
                </div>
                <div className={cn('font-mono tabular-nums text-base', today && 'font-medium')}>
                  {d.getDate()}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* The grid renders at HOUR_HEIGHT * visibleHours (up to ~1344px for
         24h at 56px/h). When the outer page wrapper uses a bounded height,
         this container has to provide the scroll context — otherwise the
         tail of the day (~11pm and later) gets clipped by the parent's
         overflow-hidden. */}
      <div ref={gridScrollRef} className="flex-1 min-h-0 overflow-y-auto">
        <div
          className="relative grid"
          style={{
            gridTemplateColumns: `48px repeat(${days.length}, 1fr)`,
            height: hourHeight * visibleHours,
          }}
          onPointerMove={(e) => {
            // Touch hat kein Hover; während Action/Split zeigen Preview-Pill
            // bzw. Split-UI die Zeit; readOnly-Grids legen nichts an — der
            // Marker wäre dort irreführend.
            if (e.pointerType === 'touch' || readOnly || action || splitTarget) {
              if (hoverSlot) setHoverSlot(null)
              return
            }
            const col = columnUnderX(e.clientX)
            if (col == null) {
              if (hoverSlot) setHoverSlot(null)
              return
            }
            const y = yInColumn(e.clientY, col)
            if (y == null) return
            const mins = yToMinutes(y)
            // Nur bei Slot-/Tageswechsel rendern — Gaming-Mäuse feuern
            // 500–1000 pointermove/s (siehe Action-Handler oben).
            if (!hoverSlot || hoverSlot.dayIndex !== col || hoverSlot.mins !== mins) {
              setHoverSlot({ dayIndex: col, mins })
            }
          }}
          onPointerLeave={() => setHoverSlot(null)}
        >
          <div
            className="relative"
            onPointerDown={(e) => {
              // Tap auf die Zeitachse verlässt den Touch-Selektionsmodus —
              // wichtig bei spaltenfüllenden Blöcken, wo „daneben" sonst
              // keine freie Fläche mehr existiert. Maus: no-op.
              if (e.pointerType === 'touch') setSelected(null)
            }}
          >
            {/* Hour labels — full 24h range */}
            {Array.from({ length: visibleHours }, (_, i) => visibleStartHour + i).map((h) => (
              <div
                key={`hour-${h}`}
                className="absolute right-2 -translate-y-1/2 text-2xs text-text-tertiary font-mono tabular-nums select-none"
                style={{ top: (h - visibleStartHour) * hourHeight }}
              >
                {h > visibleStartHour && `${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
            {/* Hover-Zeitmarker: HH:MM-Pill + Tick am rechten Rand der
                Zeitachse, exakt auf Höhe des gesnappten 15-min-Slots. Die
                Pill hat deckenden Hintergrund, damit sie ein darunterliegendes
                :00-Stundenlabel sauber überdeckt statt daneben zu quetschen. */}
            {hoverSlot && (
              <div
                className="absolute right-0 z-10 -translate-y-1/2 pointer-events-none flex items-center gap-0.5"
                style={{ top: minutesToY(hoverSlot.mins) }}
              >
                <span className="rounded bg-bg-overlay px-1 font-mono tabular-nums text-2xs text-accent">
                  {formatWallClock(hoverSlot.mins)}
                </span>
                <span className="h-px w-1.5 bg-accent" />
              </div>
            )}
          </div>

          {days.map((day, dayIndex) => {
            // Include any entry that overlaps this column's [00:00, 24:00]
            // window — entries crossing midnight render in both days, each
            // clamped to its visible segment. Then pack overlapping entries
            // side-by-side (Google-Calendar style) via greedy column layout.
            const laidOut = layoutDayEntries(entries, day)
            const today = isSameDay(day, now)
            const minsNow = now.getHours() * 60 + now.getMinutes()
            const holiday = holidays.get(dayKey(day)) ?? null
            // approved färbt satt, pending bekommt Streifen („ausstehend"),
            // rejected/withdrawn färben nicht (displayAbsenceForDay).
            const dayDisplay = displayAbsenceForDay(day, absences)
            const dayAbsence = dayDisplay?.absence
            const dayAbsencePending = dayDisplay?.pending ?? false

            // Compute preview block when an action lands on this column
            const preview = previewForColumn(action, dayIndex, hourHeight)

            return (
              // biome-ignore lint/a11y/noStaticElementInteractions: Gesten-Fläche (drag-to-create/Long-Press); Tastatur-Weg ist der ＋-Button.
              <div
                key={day.toISOString()}
                ref={(el) => {
                  columnsRef.current[dayIndex] = el
                }}
                className={cn(
                  // overflow-visible so per-block resize handles can sit
                  // outside the block edge ("vor"/"nach" dem Eintrag) without
                  // being clipped — the drag zone is then exactly the block.
                  // select-none: der Long-Press-Create darf keinen iOS-Text-
                  // Callout/keine Selektion auf der leeren Fläche auslösen.
                  'relative border-l border-border-subtle overflow-visible select-none',
                  today && 'bg-accent/[0.02]',
                  holiday && 'bg-warning/[0.04]',
                  action ? 'cursor-grabbing' : 'cursor-crosshair',
                )}
                style={{
                  ...(dayAbsence
                    ? dayAbsencePending
                      ? pendingAbsenceStyle(ABSENCE_COLOR[dayAbsence.type])
                      : { backgroundColor: `${ABSENCE_COLOR[dayAbsence.type]}14` }
                    : null),
                  // pan-y lets the browser handle vertical-page-scroll
                  // (the calendar lives inside the main scroll area).
                  // Without this, every drag-to-create gesture is also
                  // interpreted as a scroll attempt, which makes touch
                  // unusable. Tapping = 0-distance pointer event → still
                  // creates a default 1h block (CLICK_THRESHOLD path).
                  touchAction: 'pan-y',
                }}
                onPointerDown={(e) => onColumnPointerDown(e, dayIndex)}
                // Android feuert bei Long-Press contextmenu — das würde die
                // Create-Geste optisch überlagern (und auf manchen Geräten
                // die Pointer-Events canceln).
                onContextMenu={(e) => e.preventDefault()}
              >
                {Array.from({ length: visibleHours }, (_, i) => visibleStartHour + i).map((h) => (
                  <div
                    key={`gridline-${h}`}
                    className="absolute left-0 right-0 border-t border-border-subtle/25"
                    style={{ top: (h - visibleStartHour) * hourHeight }}
                  />
                ))}

                {today && minsNow >= visibleStartHour * 60 && minsNow < visibleEndHour * 60 && (
                  <div
                    className="absolute left-0 right-0 z-10 pointer-events-none"
                    style={{ top: minutesToY(minsNow) }}
                  >
                    <div className="h-px bg-accent" />
                    <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-accent" />
                  </div>
                )}

                {preview && (
                  <div
                    className="absolute left-1 right-1 rounded-md border border-accent bg-accent/20 pointer-events-none z-[15] flex items-center justify-center text-2xs text-accent font-mono tabular-nums"
                    style={{
                      top: preview.top,
                      height: Math.max(8, preview.height),
                    }}
                  >
                    {formatPreviewTime(preview.startMins, preview.endMins)}
                  </div>
                )}

                {meetings.map((m) => {
                  const seg = meetingSegmentForDay(m, day)
                  if (!seg) return null
                  const isActiveMeetingMove =
                    action?.kind === 'meeting-move' && action.meetingId === m.id
                  const isActiveMeetingResize =
                    action?.kind === 'meeting-resize' && action.meetingId === m.id
                  if (isActiveMeetingMove && action.dayIndex !== action.currentDayIndex) return null
                  return (
                    <MeetingBlock
                      key={`meeting-${m.id}-${day.toISOString()}`}
                      meeting={m}
                      segment={seg}
                      minutesToY={minutesToY}
                      ghosted={!!isActiveMeetingMove}
                      anyActionActive={action !== null}
                      draggable={!!onMeetingMove}
                      touchSelected={selected?.type === 'meeting' && selected.id === m.id}
                      onBarClickCapture={swallowSelectionGhostClick}
                      resizing={
                        isActiveMeetingResize && action.kind === 'meeting-resize'
                          ? { side: action.side, value: yToMinutes(action.currentY) }
                          : null
                      }
                      onClick={onMeetingClick ? () => onMeetingClick(m) : undefined}
                      onJoin={onMeetingJoin ? () => onMeetingJoin(m) : undefined}
                      onDelete={onMeetingDelete ? () => onMeetingDelete(m) : undefined}
                      onPointerDown={(ev) => {
                        ev.stopPropagation()
                        if (ev.button !== 0) return

                        // Touch: gleicher Selektionsmodus wie bei Entry-
                        // Blöcken — Tap selektiert (Action-Bar + Griffe),
                        // am selektierten Meeting startet Drag/Resize sofort,
                        // Long-Press bleibt als Abkürzung.
                        if (ev.pointerType === 'touch') {
                          const isSelected = selected?.type === 'meeting' && selected.id === m.id
                          if (isSelected) {
                            onSelectedBlockTouchDown(
                              ev,
                              (clientY) => startDragForMeeting(m, dayIndex, clientY),
                              (side, clientY) =>
                                startTouchResizeForMeeting(m, dayIndex, side, clientY),
                            )
                            return
                          }
                          beginTouchInteraction(
                            ev,
                            () => selectBlock('meeting', m.id),
                            (clientY) => startDragForMeeting(m, dayIndex, clientY),
                          )
                          return
                        }

                        if (!onMeetingMove) return
                        const y = yInColumn(ev.clientY, dayIndex)
                        if (y == null) return
                        const start = new Date(m.startTime).getTime()
                        const end = new Date(m.endTime).getTime()
                        setAction({
                          kind: 'meeting-move',
                          meetingId: m.id,
                          dayIndex,
                          origStartMins: seg.startMins,
                          durationMins: Math.round((end - start) / 60_000),
                          pointerStartY: y,
                          currentY: y,
                          currentDayIndex: dayIndex,
                        })
                      }}
                      onResizeStart={(ev) => {
                        // Touch: nicht schlucken — bubbelt zum Block-Handler
                        // (Zonen-Hit-Test am selektierten Meeting übernimmt).
                        if (ev.pointerType === 'touch') return
                        ev.stopPropagation()
                        if (ev.button !== 0 || !onMeetingResize) return
                        if (!seg.isStartDay) return
                        const y = yInColumn(ev.clientY, dayIndex)
                        if (y == null) return
                        setAction({
                          kind: 'meeting-resize',
                          side: 'start',
                          meetingId: m.id,
                          dayIndex,
                          origStartMins: seg.startMins,
                          origEndMins: seg.endMins,
                          currentY: y,
                        })
                      }}
                      onResizeEnd={(ev) => {
                        // Touch: nicht schlucken — bubbelt zum Block-Handler
                        // (Zonen-Hit-Test am selektierten Meeting übernimmt).
                        if (ev.pointerType === 'touch') return
                        ev.stopPropagation()
                        if (ev.button !== 0 || !onMeetingResize) return
                        if (!seg.isEndDay) return
                        const y = yInColumn(ev.clientY, dayIndex)
                        if (y == null) return
                        setAction({
                          kind: 'meeting-resize',
                          side: 'end',
                          meetingId: m.id,
                          dayIndex,
                          origStartMins: seg.startMins,
                          origEndMins: seg.endMins,
                          currentY: y,
                        })
                      }}
                    />
                  )
                })}

                {laidOut.map(({ entry: e, segment: seg, col, cols }) => {
                  const isActiveMove = action?.kind === 'move' && action.entryId === e.id
                  const isActiveResize = action?.kind === 'resize' && action.entryId === e.id
                  // While moving across days, hide entry from origin (we render preview separately)
                  if (isActiveMove && action.dayIndex !== action.currentDayIndex) return null
                  const proj = e.projectId ? (projects.get(e.projectId) ?? null) : null
                  return (
                    <span key={`${e.id}-${day.toISOString()}`}>
                      <TravelSlice
                        entry={e}
                        segment={seg}
                        type="to"
                        col={col}
                        cols={cols}
                        project={proj}
                        minutesToY={minutesToY}
                        ghosted={isActiveMove}
                      />
                      <EntryBlock
                        entry={e}
                        segment={seg}
                        col={col}
                        cols={cols}
                        project={proj}
                        minutesToY={minutesToY}
                        ghosted={isActiveMove}
                        anyActionActive={action !== null}
                        touchSelected={selected?.type === 'entry' && selected.id === e.id}
                        onBarClickCapture={swallowSelectionGhostClick}
                        resizing={
                          isActiveResize && action.kind === 'resize'
                            ? { side: action.side, value: yToMinutes(action.currentY) }
                            : null
                        }
                        onPointerDown={(ev) => onBlockPointerDown(ev, e, dayIndex)}
                        onResizeStart={(ev) => onResizePointerDown(ev, e, dayIndex, 'start')}
                        onResizeEnd={(ev) => onResizePointerDown(ev, e, dayIndex, 'end')}
                        onEdit={() => onEntryClick(e)}
                        onDelete={() => onDelete(e.id)}
                        onDuplicate={onDuplicate ? () => onDuplicate(e.id) : undefined}
                        onSplitStart={() => enterSplit(e.id, dayIndex, seg)}
                        split={
                          splitTarget?.entryId === e.id && splitTarget.dayIndex === dayIndex
                            ? {
                                mins: splitTarget.mins,
                                minMins: splitTarget.minMins,
                                maxMins: splitTarget.maxMins,
                                onGrabPointerDown: onSplitGrabPointerDown,
                                onSetMins: (m: number) =>
                                  setSplitTarget((cur) =>
                                    cur
                                      ? {
                                          ...cur,
                                          mins: Math.min(cur.maxMins, Math.max(cur.minMins, m)),
                                        }
                                      : cur,
                                  ),
                                onConfirm: () => {
                                  onSplit(e.id, makeDateTime(day, splitTarget.mins).toISOString())
                                  setSplitTarget(null)
                                },
                                onCancel: () => setSplitTarget(null),
                              }
                            : null
                        }
                      />
                      <TravelSlice
                        entry={e}
                        segment={seg}
                        type="from"
                        col={col}
                        cols={cols}
                        project={proj}
                        minutesToY={minutesToY}
                        ghosted={isActiveMove}
                      />
                    </span>
                  )
                })}

                {/* Pause/Lücke zwischen zwei aufeinanderfolgenden Einträgen.
                    Nur zwischen sauberen Einzelspalten-Einträgen (cols===1) —
                    bei Überlappungs-Clustern ist „die Lücke" mehrdeutig, dann
                    weggelassen. pointer-events-none, damit Klick-zum-Anlegen
                    auf dem Leerraum weiter funktioniert. */}
                {laidOut.map((cur, idx) => {
                  const next = laidOut[idx + 1]
                  if (!next || cur.cols !== 1 || next.cols !== 1) return null
                  const gapMins = next.segment.startMins - cur.segment.endMins
                  if (gapMins < MIN_GAP_MINUTES) return null
                  const top = minutesToY(cur.segment.endMins)
                  const height = minutesToY(next.segment.startMins) - top
                  return (
                    <div
                      key={`gap-${cur.entry.id}-${day.toISOString()}`}
                      className="pointer-events-none absolute inset-x-1 flex items-center justify-center"
                      style={{ top, height }}
                    >
                      <span className="rounded-full bg-bg-overlay/70 px-1.5 py-0.5 font-mono text-2xs tabular-nums text-text-tertiary">
                        Pause {formatHM(gapMins)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
      {footer}
    </div>
  )
}

interface PreviewInfo {
  top: number
  height: number
  startMins: number
  endMins: number
}

function previewForColumn(
  action: Action | null,
  dayIndex: number,
  hourHeight: number,
): PreviewInfo | null {
  if (!action) return null
  if (action.kind === 'create' && action.touch && action.currentDayIndex === dayIndex) {
    // Long-Press-Create: Preview zeigt exakt das, was commit() anlegen würde
    // — stationär Start+1h, aufgezogen die Snap-Spanne (beide Richtungen).
    const sized = isCreateDragSizing(action.currentY - action.anchorY, hourHeight, SNAP_MINUTES)
    const { startMins, endMins } = longPressCreateSpan({
      anchorMins: snap(toMinutes(action.anchorY, hourHeight)),
      currentMins: snap(toMinutes(action.currentY, hourHeight)),
      sized,
      snapMinutes: SNAP_MINUTES,
    })
    return {
      top: toY(startMins, hourHeight),
      height: toY(endMins, hourHeight) - toY(startMins, hourHeight),
      startMins,
      endMins,
    }
  }
  if (action.kind === 'create' && action.currentDayIndex === dayIndex) {
    const yA = Math.min(action.anchorY, action.currentY)
    const yB = Math.max(action.anchorY, action.currentY)
    const startMins = snap(toMinutes(yA, hourHeight))
    let endMins = snap(toMinutes(yB, hourHeight))
    if (Math.abs(yB - yA) < CLICK_THRESHOLD) endMins = startMins + 60
    if (endMins <= startMins) endMins = startMins + 30
    return {
      top: toY(startMins, hourHeight),
      height: toY(endMins, hourHeight) - toY(startMins, hourHeight),
      startMins,
      endMins,
    }
  }
  if (
    (action.kind === 'move' || action.kind === 'meeting-move') &&
    action.currentDayIndex === dayIndex
  ) {
    const deltaY = action.currentY - action.pointerStartY
    const newStart = snap(toMinutes(toY(action.origStartMins, hourHeight) + deltaY, hourHeight))
    // Mehrtägige Einträge (Dauer > 24h): Start bis 23:59 verschiebbar statt
    // auf 0 zu klemmen (Audit-Fix DST/Tagesende, 26764ce).
    const maxStart =
      action.durationMins <= DAY_MINUTES ? DAY_MINUTES - action.durationMins : DAY_MINUTES - 1
    const clampedStart = Math.max(0, Math.min(maxStart, newStart))
    const newEnd = Math.min(DAY_MINUTES, clampedStart + action.durationMins)
    return {
      top: toY(clampedStart, hourHeight),
      height: toY(newEnd, hourHeight) - toY(clampedStart, hourHeight),
      startMins: clampedStart,
      endMins: clampedStart + action.durationMins,
    }
  }
  return null
}

function snap(m: number): number {
  return Math.max(0, Math.min(24 * 60, Math.round(m / SNAP_MINUTES) * SNAP_MINUTES))
}
function toMinutes(y: number, hourHeight: number): number {
  return (y / hourHeight) * 60
}
function toY(m: number, hourHeight: number): number {
  return (m / 60) * hourHeight
}

/** Wanduhrzeit 'HH:MM' aus Tages-Minuten — NICHT formatHM aus lib/utils (das
 *  formatiert Dauern mit Vorzeichen und ohne Zero-Padding). */
function formatWallClock(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

function formatPreviewTime(startMins: number, endMins: number): string {
  return `${formatWallClock(startMins)} – ${formatWallClock(endMins)}`
}

function makeDateTime(day: Date, totalMinutes: number): Date {
  const d = new Date(day)
  d.setHours(0, 0, 0, 0)
  d.setMinutes(totalMinutes)
  return d
}

interface EntryBlockProps {
  entry: TimeEntry
  segment: Segment
  /** Column index (0-based) within an overlap cluster. */
  col: number
  /** Total columns in this entry's overlap cluster. */
  cols: number
  project: Project | null
  minutesToY: (m: number) => number
  ghosted: boolean
  anyActionActive: boolean
  /** Touch-Selektionsmodus: dieser Block ist per Tap selektiert. */
  touchSelected: boolean
  /** Schluckt den synthetischen Click des selektierenden Taps (Capture). */
  onBarClickCapture: (e: ReactMouseEvent) => void
  resizing: { side: 'start' | 'end'; value: number } | null
  onPointerDown: (e: ReactPointerEvent) => void
  onResizeStart: (e: ReactPointerEvent) => void
  onResizeEnd: (e: ReactPointerEvent) => void
  onEdit: () => void
  onDelete: () => void
  /** Scissors clicked — enter in-grid split mode. */
  onSplitStart: () => void
  /** Kopie direkt im Anschluss ans Original (readOnly: undefined). */
  onDuplicate?: () => void
  /** Non-null while THIS block is in split mode. */
  split: SplitMode | null
}

interface SplitMode {
  /** Current split minute within the day (drag snaps to 15; the time field
   *  allows any minute). */
  mins: number
  /** Legal range [start+15, end-15]. */
  minMins: number
  maxMins: number
  onGrabPointerDown: (e: ReactPointerEvent) => void
  /** Set an exact minute (from the time field); clamped by the caller. */
  onSetMins: (mins: number) => void
  onConfirm: () => void
  onCancel: () => void
}

function EntryBlock({
  entry,
  segment,
  col,
  cols,
  project,
  minutesToY,
  ghosted,
  anyActionActive,
  touchSelected,
  onBarClickCapture,
  resizing,
  onPointerDown,
  onResizeStart,
  onResizeEnd,
  onEdit,
  onDelete,
  onSplitStart,
  onDuplicate,
  split,
}: EntryBlockProps) {
  // Live preview during resize: clamp to the opposite edge so the block
  // never inverts (start past end / end before start).
  let startMins = segment.startMins
  let endMins = segment.endMins
  if (resizing?.side === 'end') {
    endMins = Math.max(startMins + SNAP_MINUTES, resizing.value)
  } else if (resizing?.side === 'start') {
    startMins = Math.min(Math.max(0, resizing.value), endMins - SNAP_MINUTES)
  }
  const top = minutesToY(startMins)
  // -1px leaves a hairline seam between vertically-adjacent entries so each
  // block (and its outside resize handles) stays visually distinct.
  const height = Math.max(18, minutesToY(endMins) - minutesToY(startMins) - 1)
  // Mini-Blöcke (15min = 18px-Klemme; bei hourHeight 28 auch 30min): das
  // normale Layout (py-1 + 16,5px-Preflight-Zeilenbox) schneidet die Schrift
  // unten ab — unter der Schwelle greift ein Kompakt-Layout.
  const compact = height < 28
  // Inline-Notizen: Minuten-Gate (Segment, nicht Gesamtdauer) + nie im
  // Kompakt-Layout — dessen flex-row legte die Notiz sonst neben den Titel.
  const inlineNotesVisible =
    !compact && showInlineNotes(endMins - startMins, !!project, entry.notes)
  // Prefer the project's explicit color; fall back to the deterministic
  // palette so projects without a color still get something stable.
  const color = project?.color ?? colorForKey(project?.id ?? null)
  const isRunning = entry.endTime === null
  // Live label during resize: derive from the (snapped) startMins/endMins so
  // the user sees the new duration update on every 15-min snap. When idle,
  // show the entry's actual full duration (multi-day entries report total
  // span, not just the visible day-segment).
  const totalMs =
    (entry.endTime ? new Date(entry.endTime).getTime() : Date.now()) -
    new Date(entry.startTime).getTime()
  const durationLabel = resizing ? formatHM(endMins - startMins) : formatHM(totalMs / 60_000)

  // Side-by-side packing for overlapping entries. cols=1 → full column width
  // minus 8px (4px breathing space on each side, like the original left-1/right-1).
  // cols>1 → split the column equally with a 2px gap between siblings.
  const colWidthPct = 100 / cols
  const inset = cols === 1 ? 4 : 2
  const left = `calc(${col * colWidthPct}% + ${inset}px)`
  const width = `calc(${colWidthPct}% - ${inset * 2}px)`

  return (
    <div
      onPointerDown={split ? undefined : onPointerDown}
      // Desktop-Fallback für Blöcke, die zu klein für die Inline-Notizen sind:
      // nativer Tooltip mit Beschreibung + Notizen (Muster absence-tooltip).
      title={entryTooltip(entry.description, entry.notes)}
      className={cn(
        'group absolute rounded-md text-left px-2 select-none transition-opacity z-[5] overflow-visible',
        // Kompakt: py-1 (8px) ließe von 18px nur 10px Content — die
        // Titelzeile wäre unten abgeschnitten.
        compact ? 'py-px' : 'py-1',
        // Hovered/active block (and its outside handles) paint above neighbours
        // so the resize edge you aim for at a shared seam wins the hit-test.
        'hover:z-[6]',
        split ? 'z-[8] cursor-default' : 'cursor-grab active:cursor-grabbing',
        ghosted && 'opacity-30',
        // Touch-Selektion: sichtbares Ring-Highlight + über den Nachbarn
        // zeichnen, damit die überstehenden Griffe den Hit-Test gewinnen.
        touchSelected && 'z-[7] ring-2 ring-accent shadow-lg',
      )}
      style={{
        top,
        height,
        left,
        width,
        backgroundColor: `${color}33`,
        borderLeft: `3px solid ${color}`,
        color: '#F5F0EB',
        // iOS: das System-Callout darf nicht mit dem 400ms-Long-Press
        // konkurrieren (Desktop-no-op).
        WebkitTouchCallout: 'none',
        // Nur der SELEKTIERTE Block friert das native Scrollen ein, damit
        // pointermove sofort als Drag/Resize ankommt — nicht-selektierte
        // Blöcke behalten das pan-y der Tagesspalte.
        ...(touchSelected ? { touchAction: 'none' as const } : null),
      }}
    >
      <div
        className={cn(
          'overflow-hidden h-full transition-opacity duration-150',
          // Kompakt: Titelzeile vertikal mittig statt oben angeschnitten.
          compact && 'flex items-center',
          // Hover-Dim ist eine Desktop-Affordance (legt die Action-Buttons
          // frei). Auf Touch löst der Selektions-Tap Sticky-:hover aus —
          // ohne den !touchSelected-Gate wäre der Titel genau im
          // selektierten Zustand unlesbar (Opacity 0.25).
          !anyActionActive && !touchSelected && 'group-hover:opacity-25',
        )}
      >
        {/* pr-14 reserves a corner for the absolute-positioned duration label
            on the bottom row; pr-10 on the others keeps it clear of text. */}
        <p
          className={cn(
            'text-[11px] font-medium flex items-center gap-1.5 pr-10 min-w-0',
            // Kompakt: 16,5px-Preflight-Zeilenbox auf Glyphenhöhe eindampfen,
            // sonst clippt der overflow-hidden-Wrapper die Unterlängen.
            compact && 'leading-none',
          )}
        >
          {isRunning && (
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-accent animate-pulse shrink-0" />
          )}
          {/* truncate direkt auf dem Flex-Kind — auf dem Flex-Container selbst
              greift text-overflow nicht (anonymes Flex-Item wird nur geclippt). */}
          <span className="min-w-0 truncate">{entry.description}</span>
          {/* Notiz-Hinweis, wenn die Notizen nicht inline sichtbar sind: eine
              leere Kachel suggeriert sonst, dass es nichts zum Antippen gibt —
              und der title-Tooltip ist Desktop-Hover-only, auf Touch ist das
              Icon das einzige Signal (Ticket Tim). */}
          {!inlineNotesVisible && hasNotes(entry.notes) && (
            <StickyNote
              aria-label="Notizen vorhanden"
              className="h-3 w-3 shrink-0 text-text-tertiary"
            />
          )}
        </p>
        {/* Minuten- statt Pixel-Gate (entry-notes-display.ts): der frühere
            Pixel-Vergleich verfehlte durch den 1px-Haarlinien-Abzug der
            Blockhöhe die Schwelle bei exakt 1:00/1:30 um genau 1px. Segment-
            Minuten (nicht Gesamtdauer): Multi-Day-Einträge zählen nur den
            sichtbaren Tagesausschnitt. !compact wie bei den Notizen: im
            Kompakt-Layout (flex-row, nur Titelzeile) rutschte die Zeile sonst
            NEBEN den Titel. */}
        {project && !compact && showProjectLine(endMins - startMins) && (
          <p className="text-2xs text-text-secondary truncate">{project.name}</p>
        )}
        {/* Notizen inline im Block (Ticket: tägliche Aufgaben ohne Edit-Modus
            lesbar). Gate eine Stufe über der Projektzeile, damit kurze Blöcke
            nicht zulaufen — kleinere Blöcke zeigen stattdessen das StickyNote-
            Icon in der Titelzeile (+ title-Tooltip als Desktop-Fallback).
            whitespace-pre-line rendert die \n aus dem Textarea; pb-4 hält die
            letzte sichtbare Zeile vom absoluten Dauer-Label frei (rechts
            reserviert pr-10 wie beim Titel), das harte Clipping übernimmt der
            overflow-hidden-Container. */}
        {inlineNotesVisible && (
          <p className="text-2xs italic text-text-tertiary whitespace-pre-line break-words pr-10 pb-4">
            {entry.notes}
          </p>
        )}
      </div>
      <span
        className={cn(
          'absolute right-2 font-mono tabular-nums text-2xs text-text-secondary pointer-events-none transition-opacity duration-150 px-1 rounded bg-bg-base/40',
          // Kompakt: zentrieren — bottom-1 klebte in 18px-Blöcken mitten in
          // der Titelzeile. Nicht ausblenden: der title-Tooltip
          // (entryTooltip) enthält keine Dauer.
          compact ? 'top-1/2 -translate-y-1/2' : 'bottom-1',
          // Wie beim Label: kein Hover-Dim im Touch-Selektionszustand,
          // sonst wird die Dauer beim Tap unlesbar.
          !anyActionActive && !touchSelected && 'group-hover:opacity-25',
        )}
      >
        {durationLabel}
      </span>

      {/* Bei laufenden Einträgen bleiben Resize/Move/Split gesperrt (Geometrie
          ohne definiertes Ende), aber Bearbeiten (Inhalt) + Löschen sind erlaubt
          — so lässt sich die Beschreibung eines laufenden Timers nachtragen. */}
      {!ghosted && !anyActionActive && !split && (
        <div
          className={cn(
            'absolute right-0.5 flex flex-col gap-0.5 z-10 rounded-full',
            // Desktop: hover-gated wie bisher. Touch: sichtbar UND klickbar
            // erst im Selektionsmodus (gleiche Mechanik wie die Meeting-
            // Action-Bar) — sonst schluckt der stopPropagation-pointerdown
            // der 40px-Buttons den Tap-zum-Selektieren.
            'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
            'transition-opacity duration-150',
            touchSelected && 'touch:pointer-events-auto touch:opacity-100',
            // Am Phone als Zeile (3×40px hoch statt 120px-Spalte am Block).
            'touch:flex-row',
            'bg-bg-base/90 backdrop-blur-sm shadow-sm p-0.5',
          )}
          onClickCapture={onBarClickCapture}
          style={{ top: RESIZE_HANDLE_HEIGHT + 1 }}
        >
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
            aria-label="Eintrag bearbeiten"
            className="flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full text-text-secondary hover:text-accent hover:bg-accent/15"
          >
            <Pencil className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
          </button>
          {!isRunning &&
            (() => {
              // Disabled statt stummem No-Op: ein zu kurzes Segment (z.B.
              // 23:50–00:20 auf dem Start-Tag) hat keinen legalen Trennpunkt
              // — vorher war die Schere klickbar und tat einfach nichts.
              const splittable =
                splitRangeForSegment(
                  segment.startMins,
                  segment.endMins,
                  segment.isStartDay,
                  segment.isEndDay,
                ) != null
              return (
                <button
                  type="button"
                  disabled={!splittable}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (splittable) onSplitStart()
                  }}
                  aria-label="Eintrag teilen"
                  title={
                    splittable
                      ? 'Teilen – Trennlinie ziehen'
                      : 'Segment zu kurz zum Teilen — im anderen Tages-Segment teilen'
                  }
                  className={cn(
                    'flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full',
                    splittable
                      ? 'text-text-secondary hover:text-accent hover:bg-accent/15'
                      : 'text-text-tertiary opacity-40 cursor-not-allowed',
                  )}
                >
                  <Scissors className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
                </button>
              )
            })()}
          {!isRunning && onDuplicate && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onDuplicate()
              }}
              aria-label="Eintrag duplizieren"
              title="Duplizieren – Kopie direkt im Anschluss (Tipp: Strg+Ziehen kopiert an beliebige Stelle)"
              className="flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full text-text-secondary hover:text-accent hover:bg-accent/15"
            >
              <Copy className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </button>
          )}
          {!isRunning && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              aria-label="Eintrag löschen"
              className="flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full text-danger hover:bg-danger/20"
            >
              <Trash2 className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </button>
          )}
        </div>
      )}

      {/* Resize handles sit OUTSIDE the block (above/below) so the entire
         block area is unambiguous drag-target. */}
      {!isRunning && !split && segment.isStartDay && (
        <div
          onPointerDown={onResizeStart}
          className="absolute left-1 right-1 cursor-ns-resize opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:bg-accent/40 rounded-full transition-colors"
          style={{
            top: -RESIZE_HANDLE_OUTSIDE,
            height: RESIZE_HANDLE_OUTSIDE,
            touchAction: 'none',
          }}
          title="Startzeit ändern"
        />
      )}
      {!isRunning && !split && segment.isEndDay && (
        <div
          onPointerDown={onResizeEnd}
          className="absolute left-1 right-1 cursor-ns-resize opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:bg-accent/40 rounded-full transition-colors"
          style={{
            bottom: -RESIZE_HANDLE_OUTSIDE,
            height: RESIZE_HANDLE_OUTSIDE,
            touchAction: 'none',
          }}
          title="Endzeit ändern"
        />
      )}

      {/* Große Touch-Resize-Griffe — nur im Selektionsmodus (also nie am
         Desktop). 32px-Hit-Zone mittig auf der Blockkante; die Elemente
         haben keinen eigenen Handler: Events bubbeln zum Block-pointerdown,
         der Zonen-Hit-Test (resizeZoneAt) löst die Seite auf. */}
      {touchSelected && !isRunning && !split && segment.isStartDay && (
        <div
          aria-hidden
          className="absolute left-0 right-0 z-[9] flex items-center justify-center"
          style={{ top: -TOUCH_RESIZE_OVERHANG, height: TOUCH_RESIZE_HIT, touchAction: 'none' }}
        >
          <span className="h-1.5 w-12 rounded-full border border-bg-base/60 bg-accent shadow-md" />
        </div>
      )}
      {touchSelected && !isRunning && !split && segment.isEndDay && (
        <div
          aria-hidden
          className="absolute left-0 right-0 z-[9] flex items-center justify-center"
          style={{ bottom: -TOUCH_RESIZE_OVERHANG, height: TOUCH_RESIZE_HIT, touchAction: 'none' }}
        >
          <span className="h-1.5 w-12 rounded-full border border-bg-base/60 bg-accent shadow-md" />
        </div>
      )}

      {/* In-grid split mode: a draggable split line with live half-durations
         and Teilen/Abbrechen. Follows the resize pattern (Y→snapped minute);
         the grabber works for mouse + touch via pointer events. */}
      {split &&
        (() => {
          const splitOffset = minutesToY(split.mins) - top
          const upperMs = (split.mins - startMins) * 60_000
          const lowerMs = (endMins - split.mins) * 60_000
          const hhmm = (m: number) =>
            `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
          const timeValue = hhmm(split.mins)
          return (
            <>
              {/* full-width split line */}
              <div
                className="absolute left-0 right-0 z-[17] h-0.5 bg-accent pointer-events-none"
                style={{ top: splitOffset, transform: 'translateY(-50%)' }}
              />
              {/* live half-duration labels straddling the line */}
              <span
                className="absolute right-1 z-[18] -translate-y-[calc(100%+3px)] rounded bg-bg-base/80 px-1 font-mono tabular-nums text-2xs text-accent pointer-events-none"
                style={{ top: splitOffset }}
              >
                {formatHM(upperMs / 60_000)}
              </span>
              <span
                className="absolute right-1 z-[18] translate-y-[3px] rounded bg-bg-base/80 px-1 font-mono tabular-nums text-2xs text-accent pointer-events-none"
                style={{ top: splitOffset }}
              >
                {formatHM(lowerMs / 60_000)}
              </span>
              {/* draggable grabber */}
              <button
                type="button"
                onPointerDown={split.onGrabPointerDown}
                className="absolute left-1/2 z-[19] flex h-6 w-6 touch:h-10 touch:w-10 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize items-center justify-center rounded-full border-2 border-accent bg-bg-base shadow"
                style={{ top: splitOffset, touchAction: 'none' }}
                title="Trennpunkt ziehen"
                aria-label="Trennpunkt ziehen"
              >
                <Scissors className="h-3 w-3 touch:h-4 touch:w-4 text-accent" />
              </button>
              {/* Exact-time field + confirm/cancel, sitting BELOW the grabber
                 (translateY clears the 24px grabber — 40px on touch, hence
                 the touch: offset) so they never overlap it. The field is
                 two-way bound to the split line. */}
              <div
                className="absolute left-1/2 z-[20] flex flex-col items-center gap-1 -translate-x-1/2 translate-y-4 touch:translate-y-6"
                style={{ top: splitOffset }}
              >
                <input
                  type="time"
                  value={timeValue}
                  min={hhmm(split.minMins)}
                  max={hhmm(split.maxMins)}
                  onPointerDown={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map(Number)
                    if (Number.isFinite(h) && Number.isFinite(m)) split.onSetMins(h * 60 + m)
                  }}
                  aria-label="Trennzeitpunkt"
                  title="Trennzeitpunkt exakt einstellen"
                  className="h-6 touch:h-10 touch:px-2 rounded border border-accent/60 bg-bg-base px-1 text-center text-[11px] font-mono tabular-nums text-text-primary shadow [color-scheme:dark] focus-visible:border-accent focus-visible:outline-none"
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      split.onConfirm()
                    }}
                    className="rounded-full bg-accent px-2 py-0.5 touch:px-3.5 touch:py-3 touch:text-xs text-2xs font-medium text-bg-base shadow hover:bg-accent/90"
                  >
                    Teilen
                  </button>
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      split.onCancel()
                    }}
                    className="rounded-full bg-bg-base/90 px-2 py-0.5 touch:px-3.5 touch:py-3 touch:text-xs text-2xs font-medium text-text-secondary shadow hover:text-text-primary"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </>
          )
        })()}
    </div>
  )
}

interface TravelSliceProps {
  entry: TimeEntry
  segment: Segment
  type: 'to' | 'from'
  col: number
  cols: number
  project: Project | null
  minutesToY: (m: number) => number
  ghosted: boolean
}

// Renders the optional travel-time wrap around an entry — diagonally hatched
// slice above the block ("Anfahrt") and/or below it ("Abfahrt"). Skipped
// when the entry doesn't own this segment edge (multi-day entries only show
// the prefix on the start day and the suffix on the end day).
function TravelSlice({
  entry,
  segment,
  type,
  col,
  cols,
  project,
  minutesToY,
  ghosted,
}: TravelSliceProps) {
  const isPrefix = type === 'to'
  const minutes = isPrefix ? entry.travelTimeToMinutes : entry.travelTimeFromMinutes
  if (!minutes || minutes <= 0) return null
  // Prefix only on the entry's actual start day; suffix only on its end day.
  if (isPrefix && !segment.isStartDay) return null
  if (!isPrefix && !segment.isEndDay) return null
  const baseMins = isPrefix ? segment.startMins : segment.endMins
  const sliceStart = isPrefix ? Math.max(0, baseMins - minutes) : baseMins
  const sliceEnd = isPrefix ? baseMins : Math.min(DAY_MINUTES, baseMins + minutes)
  if (sliceEnd <= sliceStart) return null
  const top = minutesToY(sliceStart)
  const height = Math.max(8, minutesToY(sliceEnd) - minutesToY(sliceStart))
  // Prefer the project's explicit color; fall back to the deterministic
  // palette so projects without a color still get something stable.
  const color = project?.color ?? colorForKey(project?.id ?? null)
  // Match the entry block's side-by-side packing so travel slices align.
  const colWidthPct = 100 / cols
  const inset = cols === 1 ? 4 : 2
  const left = `calc(${col * colWidthPct}% + ${inset}px)`
  const width = `calc(${colWidthPct}% - ${inset * 2}px)`
  return (
    <div
      aria-hidden
      className={cn(
        'absolute pointer-events-none z-[4] flex items-center justify-center text-2xs font-mono text-text-secondary overflow-hidden',
        isPrefix ? 'rounded-t-md' : 'rounded-b-md',
        ghosted && 'opacity-30',
      )}
      style={{
        top,
        height,
        left,
        width,
        // Hatched fill keyed off the project color; muted vs. the solid main
        // block. Ungeflaggte (private) Reisezeit wird zusätzlich gedämpft +
        // gestrichelt — sie zählt nicht als Arbeitszeit.
        backgroundColor: `${color}14`,
        backgroundImage: `repeating-linear-gradient(135deg, ${color}${entry.travelCountsAsWork ? '40' : '26'} 0 4px, transparent 4px 9px)`,
        borderLeft: entry.travelCountsAsWork ? `3px solid ${color}80` : `3px dashed ${color}50`,
      }}
      title={`${isPrefix ? 'Anfahrt' : 'Abfahrt'}: ${minutes} Min.${entry.travelCountsAsWork ? '' : ' (zählt nicht als Arbeitszeit)'}`}
    >
      {height >= 16 && (
        <span className="bg-bg-base/60 px-1 rounded">
          {isPrefix ? '↑' : '↓'} {minutes}m
        </span>
      )}
    </div>
  )
}

interface MeetingBlockProps {
  meeting: Meeting
  segment: Segment
  minutesToY: (m: number) => number
  ghosted?: boolean
  anyActionActive?: boolean
  draggable?: boolean
  /** Touch-Selektionsmodus: dieses Meeting ist per Tap selektiert. */
  touchSelected?: boolean
  /** Schluckt den synthetischen Click des selektierenden Taps (Capture). */
  onBarClickCapture?: (e: ReactMouseEvent) => void
  resizing?: { side: 'start' | 'end'; value: number } | null
  onClick?: () => void
  onJoin?: () => void
  onDelete?: () => void
  onPointerDown?: (e: ReactPointerEvent) => void
  onResizeStart?: (e: ReactPointerEvent) => void
  onResizeEnd?: (e: ReactPointerEvent) => void
}

// Ghost-block behind time entries — distinct visual (dashed border, Video
// icon, low-saturation accent fill). Desktop: draggable / resizable when
// callbacks are provided. Touch: tap selektiert (Action-Bar + große Griffe),
// am selektierten Meeting startet Drag/Resize direkt.
function MeetingBlock({
  meeting,
  segment,
  minutesToY,
  ghosted,
  anyActionActive,
  draggable,
  touchSelected,
  onBarClickCapture,
  resizing,
  onClick,
  onJoin,
  onDelete,
  onPointerDown,
  onResizeStart,
  onResizeEnd,
}: MeetingBlockProps) {
  let startMins = segment.startMins
  let endMins = segment.endMins
  if (resizing?.side === 'end') {
    endMins = Math.max(startMins + SNAP_MINUTES, resizing.value)
  } else if (resizing?.side === 'start') {
    startMins = Math.min(Math.max(0, resizing.value), endMins - SNAP_MINUTES)
  }
  const top = minutesToY(startMins)
  const height = Math.max(8, minutesToY(endMins) - minutesToY(startMins))
  return (
    <div
      onPointerDown={onPointerDown}
      title={`Meeting: ${meeting.title}`}
      className={cn(
        'group absolute left-0.5 right-0.5 z-[2] overflow-visible rounded-md text-left transition-colors',
        'border border-dashed border-accent/50 bg-accent/[0.07] hover:bg-accent/[0.12]',
        // Auf Touch-Geräten keine Textselektion beim Long-Press anstoßen
        // (Desktop unverändert — `touch:` greift nur bei hover:none).
        'touch:select-none',
        draggable
          ? 'cursor-grab active:cursor-grabbing'
          : onClick
            ? 'cursor-pointer'
            : 'cursor-default',
        ghosted && 'opacity-30',
        touchSelected && 'z-[7] ring-2 ring-accent shadow-lg',
      )}
      style={{
        top,
        height,
        // iOS-Callout vs. Long-Press — siehe EntryBlock.
        WebkitTouchCallout: 'none',
        ...(touchSelected ? { touchAction: 'none' as const } : null),
      }}
    >
      <div
        className={cn(
          'overflow-hidden h-full transition-opacity duration-150',
          // Kein Hover-Dim im Touch-Selektionszustand (Sticky-:hover nach
          // Tap) — Selektion = Ring + volle Lesbarkeit, siehe EntryBlock.
          !anyActionActive && draggable && !touchSelected && 'group-hover:opacity-25',
        )}
      >
        <div className="flex items-start gap-1 px-1.5 py-0.5 text-2xs text-accent font-medium">
          <Video className="h-3 w-3 mt-0.5 shrink-0" />
          <span className="truncate">{meeting.title}</span>
          {meeting.countsAsWork && (
            <span
              className="ml-auto shrink-0 rounded bg-accent/15 px-1 font-mono text-[9px] uppercase tracking-wide"
              title="Zählt als Arbeitszeit (für alle Teilnehmer)"
            >
              Ist
            </span>
          )}
        </div>
      </div>

      {!ghosted && !anyActionActive && (onClick || onJoin || onDelete) && (
        <div
          className={cn(
            'absolute right-0.5 flex flex-col gap-0.5 z-10 rounded-full',
            // Desktop: hover-gated wie bisher. Touch: sichtbar UND klickbar
            // erst im Selektionsmodus (ODER-Erweiterung, kein Ersatz) —
            // pointer-events folgt der Sichtbarkeit, damit unsichtbare
            // Buttons auf Touch nicht den Tap-zum-Selektieren schlucken.
            'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
            'transition-opacity duration-150',
            touchSelected && 'touch:pointer-events-auto touch:opacity-100',
            // Am Phone als Zeile (3×40px hoch statt 120px-Spalte am Block).
            'touch:flex-row',
            'bg-bg-base/90 backdrop-blur-sm shadow-sm p-0.5',
          )}
          onClickCapture={onBarClickCapture}
          style={{ top: 2 }}
        >
          {onJoin && (meeting.onlineUrl || meeting.roomSlug) && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onJoin()
              }}
              aria-label="Meeting beitreten"
              title="Meeting beitreten"
              className="flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full text-text-secondary hover:text-success hover:bg-success/15"
            >
              <Video className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </button>
          )}
          {onClick && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
              aria-label="Meeting bearbeiten"
              title="Meeting bearbeiten"
              className="flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full text-text-secondary hover:text-accent hover:bg-accent/15"
            >
              <Pencil className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              aria-label="Meeting löschen"
              title="Meeting löschen"
              className="flex items-center justify-center w-5 h-5 touch:w-10 touch:h-10 rounded-full text-text-secondary hover:text-danger hover:bg-danger/15"
            >
              <Trash2 className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
            </button>
          )}
        </div>
      )}

      {/* Same outside-the-block resize handles as on time-entry blocks
         (RESIZE_HANDLE_OUTSIDE px above/below the block) so the meeting
         body stays a clean drag-target. */}
      {draggable && segment.isStartDay && onResizeStart && (
        <div
          onPointerDown={onResizeStart}
          className="absolute left-1 right-1 cursor-ns-resize opacity-0 group-hover:opacity-100 hover:bg-accent/40 rounded-full transition-colors"
          style={{
            top: -RESIZE_HANDLE_OUTSIDE,
            height: RESIZE_HANDLE_OUTSIDE,
            touchAction: 'none',
          }}
          title="Startzeit ändern"
        />
      )}
      {draggable && segment.isEndDay && onResizeEnd && (
        <div
          onPointerDown={onResizeEnd}
          className="absolute left-1 right-1 cursor-ns-resize opacity-0 group-hover:opacity-100 hover:bg-accent/40 rounded-full transition-colors"
          style={{
            bottom: -RESIZE_HANDLE_OUTSIDE,
            height: RESIZE_HANDLE_OUTSIDE,
            touchAction: 'none',
          }}
          title="Endzeit ändern"
        />
      )}

      {/* Große Touch-Resize-Griffe — analog EntryBlock: nur im
         Selektionsmodus, Events bubbeln zum Block-pointerdown mit
         Zonen-Hit-Test. */}
      {touchSelected && draggable && segment.isStartDay && (
        <div
          aria-hidden
          className="absolute left-0 right-0 z-[9] flex items-center justify-center"
          style={{ top: -TOUCH_RESIZE_OVERHANG, height: TOUCH_RESIZE_HIT, touchAction: 'none' }}
        >
          <span className="h-1.5 w-12 rounded-full border border-bg-base/60 bg-accent shadow-md" />
        </div>
      )}
      {touchSelected && draggable && segment.isEndDay && (
        <div
          aria-hidden
          className="absolute left-0 right-0 z-[9] flex items-center justify-center"
          style={{ bottom: -TOUCH_RESIZE_OVERHANG, height: TOUCH_RESIZE_HIT, touchAction: 'none' }}
        >
          <span className="h-1.5 w-12 rounded-full border border-bg-base/60 bg-accent shadow-md" />
        </div>
      )}
    </div>
  )
}
